import moment from 'moment';
import carriers from '../../../../../../static/data/carriers.json';
import { billingLine } from '../../../../../../models/entities/common_subentities/billingLine';
import { address } from '../../../../../../models/entities/businessPartner/address';
import { businessPartner } from '../../../../../../models/entities/businessPartner/businessPartner';
import { shipment } from '../../../../../../models/entities/shipment/shipment';
import { BpInvoiceIssuer, BpInvoicePdfInput, resolveShipmentDisplayNumber } from './invoicePdfTypes';

/**
 * Default Orner letterhead / bank details used when the generating tenant
 * matches Orner, or as fallback for missing tenant company fields.
 */
export const INVOICE_LETTERHEAD = {
    name: 'Orner Import Solutions Ltd.',
    nameHe: 'אורנר פתרונות יבוא בע"מ',
    addressLine: 'HaOdem St. 8, Ashdod 7761208, Israel',
    addressLineHe: 'העדם 8, אשדוד 7761208 ישראל',
    tel: '+972-8-XXX XXXX',
    fax: '+972-8-XXX XXXX',
    email: 'info@or-ner.co.il',
    website: 'www.or-ner.co.il',
    accountingEmail: 'accounting@or-ner.co.il',
    tagline: 'Logistics You Can See Through'
};

/** Orner-only bank details. Never render for non-Orner tenants (no tenant bank records yet). */
export const INVOICE_PAYMENT_INSTRUCTIONS = {
    bankName: 'Bank Hapoalim B.M.',
    bankNameHe: 'בנק הפועלים בע"מ',
    bankAddress: '29 Rothschild Blvd., Tel Aviv 6688107, Israel',
    bankAddressHe: 'רוטשילד 29, תל אביב 6688107, ישראל',
    beneficiary: 'Orner Import Solutions Ltd.',
    beneficiaryHe: 'אורנר פתרונות יבוא בע"מ',
    bankNo: '12',
    branchNo: '660',
    accountNo: '337318',
    iban: 'IL880126600000033718',
    swift: 'POALILIT'
};

export type ResolvedInvoiceLetterhead = {
    name: string;
    nameHe?: string;
    addressLine: string;
    addressLineHe?: string;
    tel: string;
    fax?: string;
    email: string;
    website?: string;
    accountingEmail: string;
    /** True when issuer is Orner — enables Orner logo + Hebrew defaults. */
    isOrnerTenant: boolean;
    logoUrl?: string;
};

const isOrnerIssuerName = (name: string | undefined): boolean => {
    const n = (name || '').trim().toLowerCase();
    if (!n) return false;
    return n.includes('orner') || n.includes('or-ner') || n.includes('אורנר');
};

/**
 * Letterhead for the tenant generating the invoice.
 * Prefer issuer (logged-in company); fall back to Orner template only for
 * Orner tenants or missing contact fields.
 */
export function resolveInvoiceLetterhead(issuer: BpInvoiceIssuer): ResolvedInvoiceLetterhead {
    const isOrnerTenant = isOrnerIssuerName(issuer.name);
    const name = issuer.name?.trim() || (isOrnerTenant ? INVOICE_LETTERHEAD.name : 'Cargozone');

    if (isOrnerTenant) {
        return {
            name: issuer.name?.trim() || INVOICE_LETTERHEAD.name,
            nameHe: INVOICE_LETTERHEAD.nameHe,
            addressLine: issuer.address?.trim() || INVOICE_LETTERHEAD.addressLine,
            addressLineHe: INVOICE_LETTERHEAD.addressLineHe,
            tel: issuer.phone?.trim() || INVOICE_LETTERHEAD.tel,
            fax: INVOICE_LETTERHEAD.fax,
            email: issuer.email?.trim() || INVOICE_LETTERHEAD.email,
            website: INVOICE_LETTERHEAD.website,
            accountingEmail: issuer.email?.trim() || INVOICE_LETTERHEAD.accountingEmail,
            isOrnerTenant: true,
            logoUrl: issuer.logoUrl
        };
    }

    return {
        name,
        addressLine: issuer.address?.trim() || '—',
        tel: issuer.phone?.trim() || '—',
        email: issuer.email?.trim() || '—',
        accountingEmail: issuer.email?.trim() || '—',
        isOrnerTenant: false,
        logoUrl: issuer.logoUrl
    };
}

const DEFAULT_VAT_PERCENT = 17;

export type InvoiceChargeRow = {
    no: number;
    description: string;
    fromDate: string;
    toDate: string;
    currency: string;
    exchangeRate: string;
    unit: string;
    qty: number;
    unitPrice: number;
    amountExclVat: number;
    vatPercent: number;
    vatAmount: number;
    amountInclVat: number;
};

export type InvoicePartyBlock = {
    companyName: string;
    contactPerson: string;
    addressLine: string;
    cityZip: string;
    country: string;
    phone: string;
    vat: string;
};

export type InvoiceDetailField = { label: string; labelHe: string; value: string };

export type InvoicePdfViewModel = {
    documentTitle: string;
    documentTitleHe: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    customerNo: string;
    customerVat: string;
    branchNo: string;
    reference: string;
    paymentTerms: string;
    currency: string;
    incoterm: string;
    salesPerson: string;
    billTo: InvoicePartyBlock;
    shipTo: InvoicePartyBlock;
    /** 3 columns × N rows, column-major, matching the printed template's field order. */
    shipmentDetailColumns: InvoiceDetailField[][];
    chargeRows: InvoiceChargeRow[];
    totals: {
        exclVat: number;
        vatAmount: number;
        inclVat: number;
        vatPercent: number;
    };
    currencyBreakdown: Array<{ currency: string; exclVat: number; vatAmount: number; inclVat: number }>;
    amountDueLabel: string;
    cargoZoneNumber: string;
    isCreditNote: boolean;
};

const dash = (value: string | number | null | undefined) => {
    if (value == null) return '—';
    const text = String(value).trim();
    return text || '—';
};

const fmtDate = (value: Date | string | null | undefined) => {
    if (!value) return '—';
    const d = moment(value);
    return d.isValid() ? d.format('DD/MM/YYYY') : '—';
};

const fmtMoney = (amount: number, currency: string) =>
    `${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

const getCarrierName = (carrierCode?: string) => {
    if (!carrierCode) return '—';
    const carrier = carriers.find((item) => item.carrierCode === carrierCode);
    return carrier?.carrierName || carrierCode;
};

const pickDefaultAddress = (addresses?: address[]) => addresses?.find((item) => item.isDefault) || addresses?.[0];

const pickFinancialContact = (businessPartner: businessPartner) =>
    businessPartner.contacts?.find((c) => c.receiveFinancialDocuments) || businessPartner.contacts?.[0];

const buildPartyBlock = (businessPartner: businessPartner, shipment: shipment, role: 'bill' | 'ship'): InvoicePartyBlock => {
    const contact = pickFinancialContact(businessPartner);
    const bpAddress = pickDefaultAddress(businessPartner.addresses);

    if (role === 'ship') {
        const shipName = shipment.consigneeName || shipment.shipperName || businessPartner.name;
        const shipParty =
            shipment.parties?.find((p) => p.type === 'CONSIGNEE') || shipment.parties?.find((p) => p.type === 'SHIPPER');
        return {
            companyName: dash(shipName),
            contactPerson: dash(shipParty?.contactName || contact?.name),
            addressLine: dash(shipment.destinationPickupAddress || shipParty?.address || bpAddress?.address),
            cityZip: dash([bpAddress?.city, businessPartner.city].filter(Boolean).join(', ')),
            country: dash(shipment.destinationCountry || businessPartner.country),
            phone: dash(contact?.phoneNumber || businessPartner.phoneNumber),
            vat: dash(businessPartner.vatNumber)
        };
    }

    return {
        companyName: dash(businessPartner.name),
        contactPerson: dash(contact?.name),
        addressLine: dash(bpAddress?.address || businessPartner.city),
        cityZip: dash([bpAddress?.city, businessPartner.city].filter(Boolean).join(', ')),
        country: dash(bpAddress?.country || businessPartner.country),
        phone: dash(contact?.phoneNumber || businessPartner.phoneNumber),
        vat: dash(businessPartner.vatNumber)
    };
};

export function buildInvoicePdfViewModel(input: BpInvoicePdfInput): InvoicePdfViewModel {
    const {
        businessPartner,
        shipment,
        billingLines,
        invoiceNumber,
        invoiceDate,
        dueDate,
        paymentTermsLabel,
        currency,
        serviceTypeLabel,
        documentKind = 'invoice'
    } = input;

    const isCreditNote = documentKind === 'credit_note';
    const vatPercent = DEFAULT_VAT_PERCENT;
    const lines = billingLines.length
        ? billingLines
        : [{ serviceType: 'Freight', totalCost: shipment.sellingRate || 0, remarks: 'Shipment charges', currency } as billingLine];

    const cargoZoneNumber = resolveShipmentDisplayNumber({
        cargoZoneNumber: shipment.CargoZoneNumber,
        shipmentId: shipment.shipmentId,
        invoiceNumber
    });

    const chargeRows: InvoiceChargeRow[] = lines.map((line, index) => {
        const desc = serviceTypeLabel ? serviceTypeLabel(line.serviceType) || line.serviceType : line.serviceType;
        const description = line.remarks ? `${desc} – ${line.remarks}` : desc;
        const lineCurrency = line.currency || currency;
        const amountExclVat = Number(line.totalCost || 0);
        const vatAmount = Math.round(amountExclVat * (vatPercent / 100) * 100) / 100;
        const amountInclVat = Math.round((amountExclVat + vatAmount) * 100) / 100;

        return {
            no: index + 1,
            description,
            fromDate: fmtDate(line.date || invoiceDate),
            toDate: fmtDate(line.date || invoiceDate),
            currency: lineCurrency,
            exchangeRate: lineCurrency === currency ? '1.0000' : '—',
            unit: dash(line.chargeBy || 'Shipment'),
            qty: 1,
            unitPrice: amountExclVat,
            amountExclVat,
            vatPercent,
            vatAmount,
            amountInclVat
        };
    });

    const exclVat = chargeRows.reduce((sum, row) => sum + row.amountExclVat, 0);
    const vatAmount = chargeRows.reduce((sum, row) => sum + row.vatAmount, 0);
    const inclVat = chargeRows.reduce((sum, row) => sum + row.amountInclVat, 0);

    const breakdownMap = new Map<string, { exclVat: number; vatAmount: number; inclVat: number }>();
    chargeRows.forEach((row) => {
        const current = breakdownMap.get(row.currency) || { exclVat: 0, vatAmount: 0, inclVat: 0 };
        breakdownMap.set(row.currency, {
            exclVat: current.exclVat + row.amountExclVat,
            vatAmount: current.vatAmount + row.vatAmount,
            inclVat: current.inclVat + row.amountInclVat
        });
    });

    const packageCount =
        shipment.counts?.packageCount ?? shipment.quantity ?? shipment.packages?.length ?? shipment.totalCartons ?? null;
    const weightValue = shipment.weight != null ? `${shipment.weight} ${shipment.weightUnit || 'KG'}` : '—';
    const volumeValue = shipment.volume != null ? `${shipment.volume} ${shipment.volumeUnit || 'CBM'}` : '—';

    /** Column-major order matches the printed template exactly (col1 → col2 → col3, top to bottom). */
    const shipmentDetailColumns: InvoiceDetailField[][] = [
        [
            { label: 'Master No.', labelHe: 'מספר מאסטר', value: dash(shipment.master) },
            { label: 'House No.', labelHe: 'מספר בית', value: dash(shipment.house) },
            { label: 'ETD', labelHe: 'תאריך יציאה', value: fmtDate(shipment.etd) },
            { label: 'ETA', labelHe: 'תאריך הגעה', value: fmtDate(shipment.eta) },
            { label: 'Port of Loading', labelHe: 'נמל העמסה', value: dash(shipment.originStationName || shipment.originStation) },
            {
                label: 'Port of Discharge',
                labelHe: 'נמל פריקה',
                value: dash(shipment.destinationStationName || shipment.destinationStation)
            },
            {
                label: 'Final Destination',
                labelHe: 'יעד סופי',
                value: dash(shipment.destinationStationName || shipment.destinationCountry)
            },
            { label: 'Total Packages', labelHe: 'סה"כ חבילות', value: dash(packageCount) }
        ],
        [
            { label: 'Gross Weight', labelHe: 'משקל ברוטו', value: weightValue },
            { label: 'Net Weight', labelHe: 'משקל נטו', value: weightValue },
            { label: 'Volume', labelHe: 'נפח', value: volumeValue },
            { label: 'Cargo Type', labelHe: 'סוג מטען', value: dash(shipment.subTypeCode || shipment.typeCode) },
            { label: 'Commodity', labelHe: 'סחורה', value: dash(shipment.commodity) },
            { label: 'BL / AWB No.', labelHe: 'מספר BL / AWB', value: dash(shipment.master || shipment.house) },
            { label: 'Agent', labelHe: 'סוכן', value: dash(shipment.handlingAgent || shipment.brokerName) }
        ],
        [
            { label: 'Vessel / Flight', labelHe: 'כלי שיט / טיסה', value: dash(shipment.vesselName) },
            { label: 'Voyage / Flight No.', labelHe: 'מספר הפלגה / טיסה', value: dash(shipment.voyageNumber) },
            { label: 'Incoterm', labelHe: 'תנאי סחר', value: dash(shipment.incoterms) },
            { label: 'Payment Terms', labelHe: 'תנאי תשלום', value: dash(paymentTermsLabel) },
            { label: 'Sales Person', labelHe: 'איש מכירות', value: dash(businessPartner.salesman) },
            { label: 'Remarks', labelHe: 'הערות', value: dash(shipment.comments || shipment.specialInstructions) }
        ]
    ];

    return {
        documentTitle: isCreditNote ? 'CREDIT NOTE' : 'TAX INVOICE',
        documentTitleHe: isCreditNote ? 'זיכוי / החזר' : 'חשבונית מס',
        invoiceNumber,
        invoiceDate: fmtDate(invoiceDate),
        dueDate: fmtDate(dueDate),
        customerNo: dash(businessPartner.BusinessPartnerNumber || businessPartner.bpNumber),
        customerVat: dash(businessPartner.vatNumber),
        branchNo: dash(pickDefaultAddress(businessPartner.addresses)?.name || '1'),
        reference: dash(shipment.clientRef || shipment.forwarderRef || cargoZoneNumber),
        paymentTerms: dash(paymentTermsLabel),
        currency,
        incoterm: dash(shipment.incoterms),
        salesPerson: dash(businessPartner.salesman),
        billTo: buildPartyBlock(businessPartner, shipment, 'bill'),
        shipTo: buildPartyBlock(businessPartner, shipment, 'ship'),
        shipmentDetailColumns,
        chargeRows,
        totals: { exclVat, vatAmount, inclVat, vatPercent },
        currencyBreakdown: [...breakdownMap.entries()].map(([breakdownCurrency, amounts]) => ({
            currency: breakdownCurrency,
            ...amounts
        })),
        amountDueLabel: fmtMoney(inclVat, currency),
        cargoZoneNumber,
        isCreditNote
    };
}

export { fmtMoney, dash };
